import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, RootState } from '../../store';
import { PiBankLight, PiArrowUpLight, PiArrowDownLight, PiReceiptLight, PiClockLight, PiChartLineUpLight } from 'react-icons/pi';
import Badge from '../shared/TailwindComponents/Badge';
import Spinner from '../shared/TailwindComponents/Spinner';

interface Props {
  organizationId: number | undefined;
}

const formatCurrency = (amount: number, currency: string = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100);
};

const FinanceOverview: React.FC<Props> = ({ organizationId }) => {
  const { t } = useTranslation();
  const account = useAppSelector((state: RootState) => state.finance.account);
  const balance = useAppSelector((state: RootState) => state.finance.balance);
  const summary = useAppSelector((state: RootState) => state.finance.summary);
  const loading = useAppSelector((state: RootState) => state.finance.loading);
  const preferredCurrency = useAppSelector((state: RootState) => state.user?.userInfo?.preferences?.general_settings?.currency) || 'usd';

  const isLoading = loading?.account || loading?.balance || loading?.summary;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="glyph" className="!h-8 !w-8" />
      </div>
    );
  }

  const isConnected = !!account;

  const availableBalance = balance?.available?.[0]?.amount || 0;
  const pendingBalance = balance?.pending?.[0]?.amount || 0;
  const currency = account?.default_currency || preferredCurrency;

  const statCards = [
    {
      label: 'Available Balance',
      value: formatCurrency(availableBalance, currency),
      icon: <PiBankLight className="w-5 h-5" />,
    },
    {
      label: 'Pending Balance',
      value: formatCurrency(pendingBalance, currency),
      icon: <PiClockLight className="w-5 h-5" />,
    },
    {
      label: 'Revenue (30 days)',
      value: formatCurrency(summary?.total_revenue || 0, currency),
      icon: <PiArrowDownLight className="w-5 h-5" />,
    },
    {
      label: 'Payouts (30 days)',
      value: formatCurrency(summary?.total_payouts || 0, currency),
      icon: <PiArrowUpLight className="w-5 h-5" />,
    },
    {
      label: 'Outstanding',
      value: formatCurrency(summary?.outstanding || 0, currency),
      icon: <PiReceiptLight className="w-5 h-5" />,
    },
    {
      label: 'Net (30 days)',
      value: formatCurrency(summary?.net_balance || 0, currency),
      icon: <PiChartLineUpLight className="w-5 h-5" />,
    },
  ];

  return (
    <div className="space-y-6 px-4 py-4">
      {/* Account status */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {account?.business_name || t('Your Organization')}
        </span>
        {isConnected ? (
          <>
            <Badge color={account.status === 'active' ? 'green' : account.status === 'pending' ? 'yellow' : 'gray'}>
              {account.status}
            </Badge>
            {account.charges_enabled && (
              <Badge color="green">{t('Payments Active')}</Badge>
            )}
            {account.payouts_enabled && (
              <Badge color="green">{t('Payouts Active')}</Badge>
            )}
          </>
        ) : (
          <Badge color="red">{t('Not Connected')}</Badge>
        )}
      </div>

      {/* Stat cards — Analytics style: clean, black, minimal */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((stat) => (
          <div
            key={stat.label}
            className="glass rounded-lg shadow-sm hover:shadow-md transition-all duration-300 p-6 group cursor-pointer"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="text-black dark:text-white">
                {stat.icon}
              </div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t(stat.label)}
              </span>
            </div>
            <p className="text-2xl font-normal text-gray-900 dark:text-white">
              {stat.value}
            </p>
          </div>
        ))}
      </div>

      {/* Bank info summary */}
      {account?.bank_last4 && (
        <div className="glass rounded-lg shadow-sm p-5">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            {t('Linked Bank Account')}
          </h3>
          <div className="flex items-center gap-4">
            <div className="text-black dark:text-white">
              <PiBankLight className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {account?.bank_name || t('Bank Account')} •••• {account?.bank_last4}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {account?.country?.toUpperCase()} · {currency.toUpperCase()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick stats from summary */}
      {summary && (
        <div className="glass rounded-lg shadow-sm p-5">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            {t('30-Day Summary')}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500 dark:text-gray-400">{t('Transactions')}</p>
              <p className="font-normal text-gray-900 dark:text-white">{summary.transaction_count}</p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400">{t('Fees')}</p>
              <p className="font-normal text-gray-900 dark:text-white">
                {formatCurrency(summary.total_fees, currency)}
              </p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400">{t('Refunds')}</p>
              <p className="font-normal text-gray-900 dark:text-white">
                {formatCurrency(summary.total_refunds, currency)}
              </p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400">{t('Period')}</p>
              <p className="font-normal text-gray-900 dark:text-white">{summary.period_days} {t('days')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceOverview;
